// src/pages/settings/AccountDetails.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Spinner,
  useToast,
  Grid,
  GridItem,
  Flex,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
// import { FaGlobe } from 'react-icons/fa';

import { db } from '../../firebaseConfig';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import useOrgClaims from '../../hooks/useOrgClaims';
import { useNavigate } from 'react-router-dom';

// Functions + Auth for Create ID flow and org-root callable
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signOut } from 'firebase/auth';

export default function AccountDetails() {
  const toast = useToast();
  const navigate = useNavigate();
  const { orgId, user } = useOrgClaims();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  // Fields
  const [companyName, setCompanyName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');

  // Refs for prefill
  const prefillAppliedRef = useRef(false);

  // Load data
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const orgSnap = await getDoc(doc(db, 'organizations', orgId));
        if (alive && orgSnap.exists()) {
          setCompanyName(orgSnap.data()?.companyName || '');
        }
        const bizSnap = await getDoc(doc(db, 'organizations', orgId, 'settings', 'business'));
        const d = bizSnap.exists() ? bizSnap.data() || {} : {};
        if (!alive) return;

        setBusinessAddress(d.tradingAddress || d.registeredAddress || '');
        setPhone(d.phone || '');
        setEmail(d.email || '');
        setWebsite(d.website || '');
      } catch (e) {
        toast({ title: 'Failed to load', description: String(e?.message || e), status: 'error' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId, toast]);

  // Apply one-time prefill (from signup)
  useEffect(() => {
    if (loading || prefillAppliedRef.current) return;
    try {
      const raw = sessionStorage.getItem('signupPrefill');
      if (!raw) return;
      const p = JSON.parse(raw || '{}');

      if (p.companyName && !companyName) setCompanyName(p.companyName);
      if (p.mobile && !phone) setPhone(p.mobile);
      if (p.email && !email) setEmail(p.email);
      if (p.website && !website) setWebsite(p.website);

      prefillAppliedRef.current = true;
      sessionStorage.removeItem('signupPrefill');
    } catch {
      // ignore
    }
  }, [loading, companyName, phone, email, website]);

  // Save
  const handleSave = async () => {
    if (!orgId) {
      toast({ title: 'Organisation not set', status: 'warning' });
      return;
    }
    if (!phone.trim() || !email.trim()) {
      toast({
        title: 'Phone and Email are required',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      // 1) Save business details (SoT) to subdoc
      await setDoc(
        doc(db, 'organizations', orgId, 'settings', 'business'),
        {
          phone: phone.trim(),
          email: email.trim(),
          website: website.trim(),
          tradingAddress: businessAddress.trim(),
          registeredAddress: businessAddress.trim(), // mirror for backwards compat
          updatedAt: serverTimestamp(),
          updatedBy: {
            uid: user?.uid || null,
            name: user?.displayName || user?.email || 'Admin',
          },
          onboardingCompleteProfile: true,
        },
        { merge: true }
      );

      // 2) Mirror companyName at org root via callable (server-side; rules-safe)
      const functions = getFunctions(undefined, 'europe-west2');
      const updateOrgProfile = httpsCallable(functions, 'adminUpdateOrgProfile');
      await updateOrgProfile({
        companyName: (companyName || '').trim(),
        // Optional fields supported by the callable can be passed later if/when added to the UI:
        // logoUrl, primaryColor, accentColor
      });

      toast({ title: 'Account saved', status: 'success' });
      // navigate('/leads', { replace: true });
    } catch (e) {
      toast({ title: 'Save failed', description: String(e?.message || e), status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Helper: wait for custom claim orgId
  const waitForOrgClaim = async () => {
    const auth = getAuth();
    const maxTries = 30;
    for (let i = 0; i < maxTries; i++) {
      const res = await auth.currentUser?.getIdTokenResult(true);
      const claimOrgId = res?.claims?.orgId;
      if (claimOrgId && String(claimOrgId).length > 0) return claimOrgId;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };

  // Create ID flow
  const handleCreateId = useCallback(async () => {
    setCreating(true);
    try {
      const functions = getFunctions(undefined, 'europe-west2');
      const callable = httpsCallable(functions, 'createOrgIdForSelf');
      const res = await callable();
      const createdOrgId = res?.data?.orgId || '';

      const claimed = await waitForOrgClaim();
      if (claimed) {
        toast({
          title: 'Organisation created',
          description: `Org ID: ${createdOrgId}`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        window.location.reload();
        return;
      }

      const auth = getAuth();
      await signOut(auth);
      toast({
        title: 'Almost done',
        description: 'Please sign in again to finish setup.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      navigate('/login', {
        replace: true,
        state: { msg: 'Please sign in again to finish setup.' },
      });
    } catch (e) {
      console.error('Create ID error:', e);
      toast({
        title: 'Could not create organisation',
        description: e?.message || String(e),
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  }, [navigate, toast]);

  if (!orgId) {
    return (
      <Box borderWidth={1} borderRadius="md" p={6} mt={6} boxShadow="md">
        <Heading size="lg" mb={4}>
          Account
        </Heading>
        <VStack align="start" spacing={4}>
          <Text fontSize="md" color="gray.600">
            An organisation is required to edit account details.
          </Text>
          <Button onClick={handleCreateId} isLoading={creating} colorScheme="blue" size="lg">
            Create ID
          </Button>
        </VStack>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box borderWidth={1} borderRadius="md" p={6} mt={6} boxShadow="md">
        <Heading size="lg" mb={4}>
          Account
        </Heading>
        <HStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg">Loading…</Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box borderWidth={1} borderRadius="lg" p={0} mt={0} boxShadow="lg" bg="white">
      <VStack align="stretch" spacing={6} px={6} py={6}>
        <HStack align="baseline" spacing={4}>
          <Heading size="lg" color="blue.600">
            Account Details
          </Heading>
          <Text fontSize="md" color="gray.600">
            orgId: {orgId}
          </Text>
        </HStack>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Company name
          </FormLabel>
          <Input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company Ltd"
            size="lg"
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Business address
          </FormLabel>
          <Input
            value={businessAddress}
            onChange={(e) => setBusinessAddress(e.target.value)}
            placeholder="123 High Street, Glasgow"
            size="lg"
          />
        </FormControl>

        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <GridItem>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                Main phone
              </FormLabel>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="01234 567890"
                size="lg"
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
                Email
              </FormLabel>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="info@company.com"
                size="lg"
              />
            </FormControl>
          </GridItem>
        </Grid>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="bold" color="gray.600">
            Website (optional)
          </FormLabel>
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none" />
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://www.company.com"
              pl="2.5rem"
            />
          </InputGroup>
        </FormControl>

        <Flex justify="flex-end" pt={4}>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving} size="lg">
            Save
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
}
